import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PROJECT_MANAGEMENT_URL } from '@insite-store360/shared';
import { Observable, catchError } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';

export interface FromAddressItem {
  id: number;
  countryId: number;
  countryName?: string;
  companyName: string;
  address?: string;
  gstIdNo?: string;
  customerCare?: string;
  customerCareFax?: string;
  website?: string;
  fromPerson?: string;
  email?: string;
  phone?: string;
  createdDateTime?: Date;
  updatedDateTime?: Date;
}

export interface CreateFromAddressRequest {
  countryId: number;
  companyName: string;
  address?: string;
  gstIdNo?: string;
  customerCare?: string;
  customerCareFax?: string;
  website?: string;
  fromPerson?: string;
  email?: string;
  phone?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FromAddressService {
  private readonly URL_PATH = `${PROJECT_MANAGEMENT_URL}/from-address`;

  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlerService
  ) { }

  /**
   * Get all from addresses with pagination
   */
  public getAll(
    pageIndex?: number,
    pageSize?: number,
    searchValue?: string,
    countryId?: number | number[]
  ): Observable<{ data: FromAddressItem[]; paginatorValue: { totalData: number; totalCount: number } }> {
    let params = new HttpParams();
    if (pageIndex !== undefined && pageIndex !== null) {
      params = params.set('pageIndex', pageIndex.toString());
    }
    if (pageSize !== undefined && pageSize !== null) {
      params = params.set('pageSize', pageSize.toString());
    }
    if (searchValue && searchValue.trim() !== '') {
      params = params.set('searchValue', searchValue.trim());
    }
    if (countryId !== undefined && countryId !== null) {
      if (Array.isArray(countryId)) {
        if (countryId.length > 0) {
          params = params.set('countryIds', countryId.join(','));
        }
      } else {
        params = params.set('countryId', countryId.toString());
      }
    }
    return this.http
      .get<{ data: FromAddressItem[]; paginatorValue: { totalData: number; totalCount: number } }>(`${this.URL_PATH}/getAll`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get from address by ID
   */
  public getById(id: number): Observable<FromAddressItem> {
    return this.http
      .get<FromAddressItem>(`${this.URL_PATH}/getById/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get from address by country ID
   */
  public getByCountryId(countryId: number): Observable<FromAddressItem | null> {
    const params = new HttpParams().set('countryId', countryId.toString());
    return this.http
      .get<FromAddressItem | null>(`${this.URL_PATH}/getByCountryId`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Create a new from address
   */
  public create(data: CreateFromAddressRequest): Observable<{ message: string; result: FromAddressItem }> {
    return this.http
      .post<{ message: string; result: FromAddressItem }>(`${this.URL_PATH}/create`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Update an existing from address
   */
  public update(id: number, data: CreateFromAddressRequest): Observable<{ message: string; result: FromAddressItem }> {
    return this.http
      .put<{ message: string; result: FromAddressItem }>(`${this.URL_PATH}/update/${id}`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Delete a from address
   */
  public delete(id: number): Observable<{ message: string }> {
    return this.http
      .delete<{ message: string }>(`${this.URL_PATH}/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }
}

